 // src/pages/onboarding/IntegrationPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import api from '../../utils/apiClient';

// Platform component
const PlatformCard = ({ name, logo, selected, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`p-4 border rounded-lg flex items-center cursor-pointer transition-all ${
        selected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
      }`}
    >
      <div className="w-12 h-12 flex items-center justify-center">
        <img src={logo} alt={name} className="w-12 h-12" />
      </div>
      <span className="ml-3 font-medium">{name}</span>
    </div>
  );
};

const IntegrationPage = () => {
  const navigate = useNavigate();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const { completeOnboarding } = useAuth();
  const { currentProject, loadCurrentProject } = useProjects();
  const [loading, setLoading] = useState(false);
  
  // Function to detect links from the sitemap
  const detectLinksFromSitemap = async () => {
    if (!currentProject?.id) return;
    
    try {
      // First get the blog info to check if there's a sitemap URL
      const blogInfoRes = await api.projects.getBlogInfo(currentProject.id);
      const sitemapUrl = blogInfoRes.data?.sitemap_url;
      
      if (sitemapUrl) {
        // If we have a sitemap URL, detect links
        await api.projects.detectLinks(currentProject.id, {
          source: 'sitemap',
          url: sitemapUrl
        });
      }
    } catch (err) {
      // Error handled silently to not interrupt onboarding flow
    }
  };
  
  const handleSkip = async () => {
    setLoading(true);
    try {
      // Detect links from sitemap if available
      await detectLinksFromSitemap();
      
      // Mark onboarding as complete
      await completeOnboarding();
      
      // Refresh current project to update onboarding status
      if (currentProject?.id) {
        await loadCurrentProject(currentProject.id, { force: true });
      }
      
      // Navigate to dashboard content planner
      navigate('/dashboard/content-planner');
    } catch (err) {
      // Error handled silently to prevent disruption
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Detect links from sitemap if available
      await detectLinksFromSitemap();
      
      // Mark onboarding as complete
      await completeOnboarding();
      
      // Refresh current project to update onboarding status
      if (currentProject?.id) {
        await loadCurrentProject(currentProject.id, { force: true });
      }
      
      // Save integration preference and complete onboarding
      navigate('/dashboard/content-planner');
    } catch (err) {
      // Error handled silently to prevent disruption
    } finally {
      setLoading(false);
    }
  };

  // Platform data
  const platforms = [
    { id: 'wordpress', name: 'WordPress', logo: '/images/integration-logos/wordpress-icon.svg' },
    { id: 'webflow', name: 'Webflow', logo: '/images/integration-logos/webflow-icon.svg' },
    { id: 'notion', name: 'Notion', logo: '/images/integration-logos/notion-icon.svg' },
    { id: 'shopify', name: 'Shopify', logo: '/images/integration-logos/shopify-icon.svg' },
    { id: 'wix', name: 'Wix', logo: '/images/integration-logos/wix-icon.svg' },
    { id: 'wordpress-com', name: 'WordPress.com', logo: '/images/integration-logos/wordpress-com-icon.svg' },
    { id: 'api-webhook', name: 'API Webhook', logo: '/images/integration-logos/webhook-icon.svg' },
  ];

  return (
    <div className="p-6 md:p-8">
      <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Create Integration</h1>
      <p className="text-center text-gray-600 mb-8">Integrate your website with Hyperrank</p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.map((platform) => (
          <PlatformCard
            key={platform.id}
            name={platform.name}
            logo={platform.logo}
            selected={selectedPlatform === platform.id}
            onClick={() => setSelectedPlatform(platform.id)}
          />
        ))}
      </div>
      
      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={handleSkip}
          disabled={loading}
          className="px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-4 disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Skip And Create Later'}
        </button>
        
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedPlatform || loading}
          className="px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default IntegrationPage;